/* eslint-disable no-unused-expressions */
/* eslint-disable no-param-reassign */
import producer from 'immer';
import { handle } from 'redux-pack';
import { LOCATION_CHANGE } from 'react-router-redux';

import {
  SET_ITEM_VALUE,
  SECTIONS,
  SLUG_TO_SECTION_MAP,
  LOAD_ASSESSMENT,
  STATUS,
  COMPLETE_ASSESSMENT,
  SET_HELP_DIALOG_OPEN,
  SET_HELP_DIALOG_CLOSE,
  defectiveInId,
  isWorkingId,
  commentId,
  UPDATE_SECTION_VISIBILITY,
  SORTED_SECTIONS,
  UPDATE_ASSESSMENT_V2_ENABLED,
} from './constants';
import {
  convertToAssessment,
  createSectionVisibilityMap,
} from './data/propertyAssessment';
import { getLastAnsweredSectionId, isItemVisible } from './selectors';

export const initialState = {
  assessments: {},
  loaded: {},
  isHelpDialogOpen: false,
};

const handleHelpDialogOpen = (draft) => {
  draft.isHelpDialogOpen = true;
};

const handleHelpDialogClose = (draft) => {
  draft.isHelpDialogOpen = false;
};

const handleLocationChange = (draft, action) => {
  const match = action.payload.pathname.match(/\/avaliacao\/(\d+)\/?(.*)?/);

  if (!match) {
    return draft;
  }

  const [, photoSessionId, slug] = match;

  const sectionId = SLUG_TO_SECTION_MAP[slug] || SECTIONS.ELECTRICAL;

  draft.assessments[photoSessionId] =
    draft.assessments[photoSessionId] || convertToAssessment();
  draft.assessments[photoSessionId].currentSectionId = sectionId;

  return draft;
};

const clearDependencies = (assessment, item) => {
  const dependencies = assessment.dependencies[item.id];

  dependencies?.forEach((dependency) => {
    const section = assessment.sections[dependency.sectionId];
    const dependencyItem = section.items[dependency.itemId];

    const shouldReset = dependencyItem.id.includes('COMMENT')
      ? !item.value.includes('OTHER')
      : true;

    if (shouldReset) {
      dependencyItem.value = Array.isArray(dependencyItem.value) ? [] : '';
    }
  });
};

const isStarted = (assessment) =>
  Object.values(assessment.sections).some((section) =>
    Object.values(section.items)
      .filter((item) => isItemVisible(assessment, item.visibleIf))
      .some((item) => item.value?.length > 0),
  );

const getStatus = (assessment) => {
  if (isStarted(assessment)) {
    return STATUS.STARTED;
  }

  return STATUS.NOT_STARTED;
};

const handleSetItemValue = (draft, action) =>
  handle(draft, action, {
    success: (state) => {
      const { photoSessionId, item } = action.payload;

      const assessment = state.assessments[photoSessionId];
      const section = assessment.sections[assessment.currentSectionId];
      const oldValue = section.items[item.id].value;

      if (assessment.status === STATUS.COMPLETED) {
        return state;
      }

      if (Array.isArray(oldValue)) {
        const hasValue = oldValue.includes(item.value);
        const newValue = hasValue
          ? oldValue.filter((v) => v !== item.value)
          : oldValue.concat(item.value);

        section.items[item.id].value = newValue;
      } else {
        section.items[item.id].value = item.value;
      }

      clearDependencies(assessment, section.items[item.id]);

      assessment.status = getStatus(assessment);

      if (!assessment.started && assessment.status === STATUS.STARTED) {
        assessment.started = new Date().toJSON();
      }

      return state;
    },
  });

const handleUpdateSectionVisibility = (draft, action) => {
  const { photoSessionId, checklist } = action.payload;

  const assessment = draft?.assessments?.[photoSessionId];

  const hasInvalidPhotoSessionId = !photoSessionId;
  const hasInvalidAssessment = !assessment;
  const hasCompletedAssessment = assessment?.status === STATUS.COMPLETED;

  if (
    hasInvalidPhotoSessionId ||
    hasInvalidAssessment ||
    hasCompletedAssessment
  ) {
    return draft;
  }

  const visibilityMap = createSectionVisibilityMap({
    checklist,
    assessment,
  });

  const emptyAssessment = convertToAssessment();

  const sections = {};

  SORTED_SECTIONS.forEach((sectionId) => {
    const isVisible = visibilityMap[sectionId];

    const shouldIncludeSection =
      !(sectionId in visibilityMap) ||
      (sectionId in visibilityMap && isVisible);

    if (shouldIncludeSection) {
      const currentSection = assessment.sections[sectionId];
      const emptySection = emptyAssessment.sections[sectionId];

      sections[sectionId] = currentSection || emptySection;
    }
  });

  draft.assessments[photoSessionId].sections = sections;

  return draft;
};

const updateCurrentSectionId = (draft, action) => {
  const photoSessionId = action.payload.photoSessionId;
  const assessment = draft.assessments[photoSessionId];

  const id = getLastAnsweredSectionId(assessment) || SECTIONS.ELECTRICAL;

  if (draft.assessments[photoSessionId].status !== STATUS.COMPLETED) {
    draft.assessments[photoSessionId].currentSectionId = id;
  }
};

const ITEMS_TO_REMOVE_BY_ID = [
  isWorkingId(SECTIONS.SWITCH_SOCKET),
  defectiveInId(SECTIONS.SWITCH_SOCKET),
  commentId(SECTIONS.SWITCH_SOCKET),
];

const removeItems = (assessment) => ({
  ...assessment,
  sections: Object.entries(assessment.sections).reduce(
    (sections, [sectionId, section]) => ({
      ...sections,
      [sectionId]: {
        ...section,
        items: Object.entries(section.items).reduce((items, [itemId, item]) => {
          if (ITEMS_TO_REMOVE_BY_ID.includes(itemId)) {
            return items;
          }

          return {
            ...items,
            [itemId]: item,
          };
        }, {}),
      },
    }),
    {},
  ),
});

const handleLoadAssessment = (draft, action) =>
  handle(draft, action, {
    success: (state) => {
      const photoSessionId = action.payload.photoSessionId;

      if (!state.assessments[photoSessionId]) {
        state.assessments[photoSessionId] = convertToAssessment();
      }

      if (state.assessments[photoSessionId]?.status === STATUS.STARTED) {
        return state;
      }

      const assessment = action.payload.assessment
        ? removeItems(action.payload.assessment)
        : convertToAssessment();

      state.assessments[photoSessionId] = {
        ...state.assessments[photoSessionId],
        ...assessment,
      };
      state.loaded[photoSessionId] = true;

      handleUpdateAssessmentV2Enabled(state, action);

      handleUpdateSectionVisibility(state, action);

      updateCurrentSectionId(state, action);

      return state;
    },
  });

const handleCompleteAssessment = (draft, action) => {
  draft.assessments[action.payload.photoSessionId].status = STATUS.COMPLETED;

  return draft;
};

const handleUpdateAssessmentV2Enabled = (draft, action) => {
  const { photoSessionId, isAssessmentV2Enabled } = action.payload;

  if (!draft?.assessments) return draft;

  const assessment = draft?.assessments[photoSessionId];

  if (!assessment) return draft;

  assessment.isV2Enabled = isAssessmentV2Enabled;

  return draft;
};

export const propertyAssessmentReducer = (draft, action) => {
  switch (action.type) {
    case SET_HELP_DIALOG_OPEN:
      return handleHelpDialogOpen(draft);
    case SET_HELP_DIALOG_CLOSE:
      return handleHelpDialogClose(draft);
    case LOCATION_CHANGE:
      return handleLocationChange(draft, action);
    case SET_ITEM_VALUE:
      return handleSetItemValue(draft, action);
    case LOAD_ASSESSMENT:
      return handleLoadAssessment(draft, action);
    case COMPLETE_ASSESSMENT:
      return handleCompleteAssessment(draft, action);
    case UPDATE_SECTION_VISIBILITY:
      return handleUpdateSectionVisibility(draft, action);
    case UPDATE_ASSESSMENT_V2_ENABLED:
      return handleUpdateAssessmentV2Enabled(draft, action);
    default:
      return draft;
  }
};

export default producer(propertyAssessmentReducer, initialState);
